import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
} from "@mui/material";

const HotelForm = ({ handleClose, handleSubmit, initialData }) => {
  const [brands, setBrands] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [formData, setFormData] = useState({
    org_id: "",
    brand_id: "",
    name: "",
    local_name: "",
    property_code: "",
    description: "",
    short_description: "",
    star_rating: "",
    property_type: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    latitude: "",
    longitude: "",
    timezone: "Asia/Kolkata", // Default timezone
    phone: "",
    email: "",
    website_url: "",
    check_in_time: "15:00",
    check_out_time: "11:00",
    min_advance_booking: 0,
    max_advance_booking: 365,
    cancellation_policy_hours: 24,
    default_currency: "INR", // Set default currency to INR
    tax_rate: "",
    resort_fee: 0,
    is_active: true,
    hotel_org: "owned", // Add hotel_org field with default value
  });

  const propertyTypes = ["HOTEL", "RESORT", "APARTMENT", "VILLA", "BOUTIQUE"];
  const hotelOrgTypes = ["owned", "others"]; // Define hotel organization types

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orgRes, brandRes, tzRes, currencyRes] = await Promise.all([
          fetch("https://api.esthell.com/HotelOrganization"),
          fetch("https://api.esthell.com/HotelBrand"),
          fetch("https://api.esthell.com/Timezones"),
          fetch("https://api.esthell.com/Currencies"),
        ]);

        const [orgData, brandData, tzData, currencyData] = await Promise.all([
          orgRes.json(),
          brandRes.json(),
          tzRes.json(),
          currencyRes.json(),
        ]);

        if (orgData.success) setOrganizations(orgData.data);
        if (brandData.success) setBrands(brandData.data);
        if (tzData.success)
          setTimezones(tzData.data.map((tz) => ({ value: tz, label: tz }))); // Format timezone data
        if (currencyData.success) setCurrencies(currencyData.data);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      is_active: e.target.checked,
    }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleSubmit(formData);
  };

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2, boxShadow: 1 }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {initialData ? "Update Hotel" : "New Hotel"}
      </Typography>

      <Grid container spacing={2}>
        {Object.entries({
          org_id: "Organization",
          brand_id: "Brand",
          name: "Hotel Name",
          local_name: "Local Name",
          property_code: "Property Code",
          description: "Description",
          short_description: "Short Description",
          star_rating: "Star Rating",
          property_type: "Property Type",
          hotel_org: "Hotel Organization Type", // Added hotel_org field
          address_line1: "Address Line 1",
          address_line2: "Address Line 2",
          city: "City",
          state: "State",
          country: "Country",
          postal_code: "Postal Code",
          latitude: "Latitude",
          longitude: "Longitude",
          timezone: "Timezone",
          phone: "Phone",
          email: "Email",
          website_url: "Website URL",
          check_in_time: "Check-In Time",
          check_out_time: "Check-Out Time",
          min_advance_booking: "Min Advance Booking (days)",
          max_advance_booking: "Max Advance Booking (days)",
          cancellation_policy_hours: "Cancellation Policy (hrs)",
          default_currency: "Currency",
          tax_rate: "Tax Rate (%)",
          resort_fee: "Resort Fee",
        }).map(([key, label]) => (
          <Grid item xs={12} sm={6} key={key}>
            <TextField
              fullWidth
              size="small"
              name={key}
              label={label}
              type={
                key === "latitude" || key === "longitude"
                  ? "number"
                  : typeof formData[key] === "number"
                    ? "number"
                    : "text"
              }
              value={formData[key]}
              onChange={handleChange}
              inputProps={
                key === "latitude" || key === "longitude"
                  ? {
                      step: "0.00000001",
                      min: key === "latitude" ? -90 : -180,
                      max: key === "latitude" ? 90 : 180,
                    }
                  : {}
              }
              select={
                key === "brand_id" ||
                key === "org_id" ||
                key === "property_type" ||
                key === "default_currency" ||
                key === "hotel_org" // Added hotel_org to select fields
              }
              sx={{ "& .MuiInputBase-root": { height: "35px" } }}
            >
              {(key === "brand_id"
                ? brands
                : key === "org_id"
                  ? organizations
                  : key === "property_type"
                    ? propertyTypes
                    : key === "timezone"
                      ? timezones
                      : key === "hotel_org"
                        ? hotelOrgTypes // Added hotel_org options
                        : key === "default_currency"
                          ? [{ value: "INR", label: "INR" }]
                          : []
              ).map((option) => (
                <MenuItem
                  key={
                    option?.brand_id ||
                    option?.org_id ||
                    option?.value ||
                    option
                  }
                  value={
                    option?.brand_id ||
                    option?.org_id ||
                    option?.value ||
                    option
                  }
                >
                  {option?.name || option?.label || option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        ))}

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={handleSwitchChange}
              />
            }
            label="Is Active"
          />
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" color="primary" type="submit">
            {initialData ? "Update Hotel" : "Create Hotel"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HotelForm;
